import React from 'react';
import { Container, Typography, Card, CardContent, Grid, Box, Button, Chip, LinearProgress } from '@mui/material';
import { Star, LocationOn, Phone, Group } from '@mui/icons-material';

const TrialMatching = ({ user, accessLevel }) => {
  const matchedTrials = [
    {
      id: 1,
      title: "Phase III Study of Pembrolizumab in Early-Stage Breast Cancer",
      matchScore: 92,
      phase: "Phase III",
      sponsor: "Merck & Co.",
      location: "Miami, FL (2.3 miles)",
      eligibility: "Eligible",
      keyFeatures: ["Immunotherapy", "Neoadjuvant", "HER2-negative"]
    },
    {
      id: 2,
      title: "CDK4/6 Inhibitor Combination Therapy for HR+ Breast Cancer",
      matchScore: 87,
      phase: "Phase II",
      sponsor: "Pfizer Inc.",
      location: "Tampa, FL (45.2 miles)",
      eligibility: "Likely Eligible",
      keyFeatures: ["Targeted Therapy", "HR-positive", "Oral Medication"]
    },
    {
      id: 3,
      title: "Novel Antibody-Drug Conjugate for Triple-Negative Breast Cancer",
      matchScore: 76,
      phase: "Phase I/II",
      sponsor: "Genentech",
      location: "Jacksonville, FL (142 miles)",
      eligibility: "Needs Review",
      keyFeatures: ["ADC Therapy", "Early Phase", "Innovative"]
    }
  ];

  const getEligibilityColor = (eligibility) => {
    switch (eligibility) {
      case 'Eligible': return 'success';
      case 'Likely Eligible': return 'warning';
      case 'Needs Review': return 'info';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        AI-Powered Trial Matching
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Personalized clinical trial recommendations based on your mCODE profile and preferences.
      </Typography>

      {/* Matching Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Matching Summary
              </Typography>
              <Typography variant="h3" color="primary">
                {matchedTrials.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Trials matched to your profile
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Best Match Score
              </Typography>
              <Typography variant="h3" color="success.main">
                {Math.max(...matchedTrials.map(t => t.matchScore))}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compatibility with top trial
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Geographic Range
              </Typography>
              <Typography variant="h3" color="info.main">
                150
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Miles search radius
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Trial Recommendations */}
      <Grid container spacing={3}>
        {matchedTrials.map((trial) => (
          <Grid item xs={12} key={trial.id}>
            <Card>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Star sx={{ color: 'gold', mr: 1 }} />
                      <Typography variant="h6">
                        {trial.title}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip label={trial.phase} color="primary" size="small" />
                      <Chip 
                        label={trial.eligibility} 
                        color={getEligibilityColor(trial.eligibility)} 
                        size="small" 
                      />
                      {trial.keyFeatures.map((feature) => (
                        <Chip 
                          key={feature} 
                          label={feature} 
                          variant="outlined" 
                          size="small" 
                        />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Group sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        Sponsor: {trial.sponsor}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {trial.location}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button variant="contained" size="small">
                        View Details
                      </Button>
                      <Button variant="outlined" size="small">
                        Contact Site
                      </Button>
                      <Button variant="text" size="small" startIcon={<Phone />}>
                        Schedule Call
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Match Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={trial.matchScore} 
                        sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                        color={trial.matchScore >= 90 ? 'success' : trial.matchScore >= 80 ? 'warning' : 'info'}
                      />
                      <Typography variant="h6" color="primary">
                        {trial.matchScore}%
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Based on clinical profile, genomic markers, treatment history, and geographic proximity.
                    </Typography>

                    <Button variant="outlined" fullWidth size="small">
                      Why This Match?
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default TrialMatching;